import { useState, useEffect, useCallback } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, apiErr } from "@/lib/api";
import { useLiveData } from "@/context/LiveDataContext";
import { resolvePilotDrone } from "@/lib/pilotageNavigation";
import MapView from "@/components/MapView";
import { num } from "@/lib/format";
import { Search, Send, Loader2, CheckCircle2, XCircle, MapPin, History, Plus, Trash2, Route, CalendarClock, GripVertical } from "lucide-react";

function haversineKm(a, b) {
  const R = 6371, r = Math.PI / 180;
  const dLat = (b[0] - a[0]) * r, dLng = (b[1] - a[1]) * r;
  const x = Math.sin(dLat / 2) ** 2 + Math.cos(a[0] * r) * Math.cos(b[0] * r) * Math.sin(dLng / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(x), Math.sqrt(1 - x));
}
function fmtDur(sec) {
  if (!sec || sec < 1) return "—";
  const m = Math.floor(sec / 60), s = Math.round(sec % 60);
  return m > 0 ? `${m} min ${s}s` : `${s}s`;
}
function fmtClock(d) {
  return d.toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" });
}

const MISSION_TYPES = [
  { v: "delivery", l: "Livraison" },
  { v: "surveillance", l: "Surveillance" },
  { v: "intervention", l: "Intervention rapide" },
  { v: "test_waypoint", l: "Test waypoint" },
];
const ARRIVAL = [
  { v: "LOITER", l: "LOITER / stationnaire" },
  { v: "RETURN_TO_HOME", l: "Retour base" },
  { v: "LAND", l: "Atterrir" },
  { v: "WAIT", l: "Attendre instruction" },
];

export default function Missions() {
  const { drones, refresh } = useLiveData();
  const navigate = useNavigate();
  const [params, setSearchParams] = useSearchParams();
  const [settings, setSettings] = useState(null);
  const [profiles, setProfiles] = useState([]);
  const [missions, setMissions] = useState([]);

  const [droneId, setDroneId] = useState(params.get("drone") || "");
  const [address, setAddress] = useState("");
  const [missionType, setMissionType] = useState("delivery");
  const [altitude, setAltitude] = useState(40);
  const [speed, setSpeed] = useState(5);
  const [rtlAlt, setRtlAlt] = useState(50);
  const [arrival, setArrival] = useState("RETURN_TO_HOME");
  const [loiter, setLoiter] = useState(5);
  const [returnHome, setReturnHome] = useState(true);
  const [comment, setComment] = useState("");

  const [dest, setDest] = useState(null);
  const [geocoding, setGeocoding] = useState(false);
  const [sending, setSending] = useState(false);

  // Mode itinéraire (multi-waypoints + planification horaire)
  const [mode, setMode] = useState("single");        // "single" | "itinerary"
  const [scheduledStart, setScheduledStart] = useState("");
  const [waypoints, setWaypoints] = useState([
    { address: "", dest: null, altitude: 40, speed: 5, target_time: "", arrival_action: "LOITER", geocoding: false },
  ]);

  const updateWp = (i, patch) => setWaypoints((w) => w.map((x, j) => (j === i ? { ...x, ...patch } : x)));
  const addWp = () => setWaypoints((w) => [...w, { address: "", dest: null, altitude, speed, target_time: "", arrival_action: "LOITER", geocoding: false }]);
  const removeWp = (i) => setWaypoints((w) => w.filter((_, j) => j !== i));

  // Glisser-déposer pour réorganiser les waypoints
  const [dragIdx, setDragIdx] = useState(null);
  const reorderWp = (from, to) => {
    if (from === to || from == null) return;
    setWaypoints((w) => {
      const a = [...w];
      const [moved] = a.splice(from, 1);
      a.splice(to, 0, moved);
      return a;
    });
  };
  const geocodeWp = async (i) => {
    const wp = waypoints[i];
    if (!wp.address.trim()) return;
    updateWp(i, { geocoding: true });
    try {
      const { data } = await api.get("/geocode", { params: { q: wp.address } });
      updateWp(i, { dest: data, geocoding: false });
      toast.success(`Waypoint ${i + 1} géocodé`, { description: data.display_name });
    } catch (e) {
      updateWp(i, { dest: null, geocoding: false });
      toast.error(`Waypoint ${i + 1} : géocodage échoué`, { description: apiErr(e) });
    }
  };

  const submitItinerary = async () => {
    const wps = waypoints.filter((w) => w.dest);
    if (wps.length === 0) { toast.error("Géocodez au moins un waypoint"); return; }
    setSending(true);
    try {
      const { data } = await api.post("/missions", {
        drone_id: droneId, mission_type: missionType,
        scheduled_start: scheduledStart ? new Date(scheduledStart).toISOString() : null,
        waypoints: wps.map((w) => ({
          lat: w.dest.lat, lng: w.dest.lng, address: w.dest.display_name,
          altitude: Number(w.altitude), speed: Number(w.speed), arrival_action: w.arrival_action,
          target_time: w.target_time ? new Date(w.target_time).toISOString() : null,
        })),
      });
      toast.success(data.scheduled ? "Itinéraire planifié" : "Itinéraire lancé",
        { description: `${data.waypoints} waypoints${data.scheduled ? " · déclenchement auto à l'heure prévue" : ""}` });
      loadMissions(); refresh();
    } catch (e) {
      toast.error("Itinéraire impossible", { description: apiErr(e) });
    } finally { setSending(false); }
  };

  const loadMissions = useCallback(async () => {
    const { data } = await api.get("/missions");
    setMissions(data.missions || []);
  }, []);

  useEffect(() => {
    api.get("/flight-settings").then(({ data }) => {
      setSettings(data.settings);
      setProfiles(data.profiles);
      setAltitude(data.settings.default_cruise_altitude_m);
      setSpeed(data.settings.default_cruise_speed_mps);
      setRtlAlt(data.settings.rtl_altitude_m);
    }).catch(() => {});
    loadMissions();
  }, [loadMissions]);

  // UNIFIED.4 : résolution robuste de ?drone=. Param valide => sélectionné ; invalide/absent => fallback.
  useEffect(() => {
    if (!drones.length) return;
    const q = params.get("drone");
    if (q && drones.some((d) => d.drone_id === q)) {
      if (droneId !== q) setDroneId(q);
      return;
    }
    const current = droneId && drones.some((d) => d.drone_id === droneId) ? droneId : null;
    const fb = resolvePilotDrone(drones, current);
    if (fb && fb !== droneId) setDroneId(fb);
  }, [drones, params, droneId]);

  // Changement de drone depuis le sélecteur => met aussi à jour l'URL (navigation applicative).
  const onDroneChange = useCallback((id) => {
    setDroneId(id);
    setSearchParams((sp) => {
      const n = new URLSearchParams(sp);
      if (id) n.set("drone", id); else n.delete("drone");
      return n;
    });
  }, [setSearchParams]);

  const drone = drones.find((d) => d.drone_id === droneId);

  const applyProfile = (p) => {
    setAltitude(p.altitude); setSpeed(p.speed); setArrival(p.arrival_action);
    setLoiter(p.loiter_minutes); setReturnHome(p.return_home_after);
  };

  const geocode = async () => {
    if (!address.trim()) return;
    setGeocoding(true);
    try {
      const { data } = await api.get("/geocode", { params: { q: address } });
      setDest(data);
      toast.success("Adresse géocodée", { description: data.display_name });
    } catch (e) {
      setDest(null);
      toast.error("Géocodage échoué", { description: apiErr(e) });
    } finally { setGeocoding(false); }
  };

  // Validation checklist
  const minBat = settings?.min_battery_for_mission_percent ?? 35;
  const checks = [
    { k: "Drone en ligne", ok: drone?.status === "online" },
    { k: "Connexion WS drone", ok: !!drone?.drone_ws_connected },
    { k: `Batterie ≥ ${minBat}%`, ok: (drone?.battery ?? 0) >= minBat },
    { k: "GPS fix", ok: !!drone?.gps_fix },
    { k: "Position drone connue", ok: drone?.lat != null && drone?.lng != null },
    { k: "Destination géocodée", ok: !!dest },
    { k: settings ? `Altitude ${settings.min_altitude_m}-${settings.max_altitude_m}m` : "Altitude (paramètres indisponibles)", ok: settings && altitude >= settings.min_altitude_m && altitude <= settings.max_altitude_m },
    { k: "Vitesse 1-12 m/s", ok: speed >= 1 && speed <= 12 },
  ];
  const allOk = checks.every((c) => c.ok);

  const distance = (drone?.lat && dest) ? haversine(drone.lat, drone.lng, dest.lat, dest.lng) : null;

  const submit = async () => {
    setSending(true);
    try {
      const { data } = await api.post("/missions", {
        drone_id: droneId, mission_type: missionType, lat: dest.lat, lng: dest.lng,
        address: dest.display_name, altitude: Number(altitude), speed: Number(speed),
        arrival_action: arrival, loiter_minutes: Number(loiter), return_home_after: returnHome,
        rtl_altitude: Number(rtlAlt), comment,
      });
      toast.success("Mission envoyée", { description: `command_id: ${data.command_id} · ${data.sent_live ? "live" : "queued"}` });
      loadMissions();
      refresh();
    } catch (e) {
      toast.error("Mission impossible", { description: apiErr(e) });
    } finally { setSending(false); }
  };

  // Récapitulatif d'itinéraire : distance totale, durée estimée, ETA par waypoint
  const geocodedWps = waypoints.filter((w) => w.dest);
  const itinSummary = (() => {
    if (mode !== "itinerary" || geocodedWps.length === 0) return null;
    let prev = drone?.lat != null ? [drone.lat, drone.lng] : null;
    let t = scheduledStart ? new Date(scheduledStart) : new Date();
    let totalKm = 0, totalSec = 0;
    const legs = geocodedWps.map((w, i) => {
      const pt = [w.dest.lat, w.dest.lng];
      const legKm = prev ? haversineKm(prev, pt) : 0;
      const spd = Number(w.speed) || 5;
      const legSec = (legKm * 1000) / spd;
      totalKm += legKm; totalSec += legSec;
      if (w.target_time) t = new Date(w.target_time);
      else t = new Date(t.getTime() + legSec * 1000);
      prev = pt;
      return { i, legKm, legSec, eta: new Date(t), forced: !!w.target_time };
    });
    return { legs, totalKm, totalSec, finalEta: legs[legs.length - 1]?.eta };
  })();

  return (
    <div className="p-6 grid grid-cols-1 xl:grid-cols-2 gap-6">
      <div className="space-y-4">
        <div>
          <h1 className="font-chivo text-3xl font-black tracking-tighter uppercase">Envoyer une mission</h1>
          <p className="font-mono text-xs text-[#A3A3A3] mt-1">Adresse unique ou itinéraire multi-waypoints planifié</p>
        </div>

        <div className="flex gap-2">
          <button data-testid="mode-single" onClick={() => setMode("single")}
            className={`flex items-center gap-2 font-mono text-[11px] uppercase tracking-wider rounded-sm px-3 py-2 border transition-colors ${mode === "single" ? "border-[#3B82F6] text-[#3B82F6] bg-[#3B82F6]/10" : "border-white/15 text-[#A3A3A3] hover:border-white/30"}`}>
            <MapPin size={13} /> Adresse unique
          </button>
          <button data-testid="mode-itinerary" onClick={() => setMode("itinerary")}
            className={`flex items-center gap-2 font-mono text-[11px] uppercase tracking-wider rounded-sm px-3 py-2 border transition-colors ${mode === "itinerary" ? "border-[#3B82F6] text-[#3B82F6] bg-[#3B82F6]/10" : "border-white/15 text-[#A3A3A3] hover:border-white/30"}`}>
            <Route size={13} /> Itinéraire
          </button>
        </div>

        {/* Profiles */}
        <div className="flex flex-wrap gap-2">
          {profiles.map((p) => (
            <button key={p.id} data-testid={`profile-${p.id}`} onClick={() => applyProfile(p)}
              className="font-mono text-[10px] uppercase tracking-wider border border-white/15 hover:border-[#3B82F6] hover:text-[#3B82F6] rounded-sm px-3 py-1.5 transition-colors">
              {p.label}
            </button>
          ))}
        </div>

        <Sel label="Drone" value={droneId} onChange={onDroneChange} testid="mission-drone"
          options={drones.map((d) => ({ v: d.drone_id, l: `${d.drone_id} (${d.status})` }))} />

        {mode === "single" && (<>
        <div className="flex gap-2 items-end">
          <div className="flex-1">
            <Lbl>Adresse de destination</Lbl>
            <input data-testid="mission-address" value={address} onChange={(e) => setAddress(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && geocode()}
              placeholder="ex: 1 rue de Paris, Le Havre"
              className="w-full bg-[#141414] border border-white/15 focus:border-[#3B82F6] outline-none rounded-sm px-3 py-2.5 font-mono text-sm" />
          </div>
          <button data-testid="geocode-btn" onClick={geocode} disabled={geocoding}
            className="flex items-center gap-2 bg-[#3B82F6] hover:bg-[#2563EB] text-white font-mono text-xs uppercase tracking-wider rounded-sm px-4 py-2.5 disabled:opacity-50">
            {geocoding ? <Loader2 size={14} className="animate-spin" /> : <Search size={14} />} Géocoder
          </button>
        </div>

        {dest && (
          <div data-testid="geocode-result" className="bg-[#0A0A0A] border border-[#3B82F6]/30 rounded-sm p-3 font-mono text-xs space-y-1">
            <div className="flex items-center gap-1.5 text-[#3B82F6]"><MapPin size={12} /> {dest.display_name}</div>
            <div className="text-[#A3A3A3]">Lat {num(dest.lat, 5)} · Lng {num(dest.lng, 5)}{distance != null && ` · ~${num(distance, 2)} km`}</div>
          </div>
        )}

        <div className="grid grid-cols-2 gap-3">
          <Sel label="Type de mission" value={missionType} onChange={setMissionType} testid="mission-type"
            options={MISSION_TYPES.map((m) => ({ v: m.v, l: m.l }))} />
          <Sel label="Action à l'arrivée" value={arrival} onChange={setArrival} testid="mission-arrival"
            options={ARRIVAL.map((m) => ({ v: m.v, l: m.l }))} />
          <NumIn label="Altitude (m)" value={altitude} onChange={setAltitude} testid="mission-altitude" />
          <NumIn label="Vitesse (m/s)" value={speed} onChange={setSpeed} testid="mission-speed" />
          <NumIn label="Altitude RTL (m)" value={rtlAlt} onChange={setRtlAlt} testid="mission-rtl-alt" />
          <NumIn label="Durée stationnaire (min)" value={loiter} onChange={setLoiter} testid="mission-loiter" />
        </div>

        <div>
          <Lbl>Commentaire</Lbl>
          <textarea data-testid="mission-comment" value={comment} onChange={(e) => setComment(e.target.value)} rows={2}
            className="w-full bg-[#141414] border border-white/15 focus:border-[#3B82F6] outline-none rounded-sm px-3 py-2 font-mono text-sm" />
        </div>

        {/* Checklist */}
        <div className="bg-[#0A0A0A] border border-white/10 rounded-sm p-3">
          <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#A3A3A3] mb-2">Vérifications pré-vol</div>
          <div className="grid grid-cols-2 gap-y-1.5 gap-x-3">
            {checks.map((c) => (
              <div key={c.k} className="flex items-center gap-1.5 font-mono text-[11px]">
                {c.ok ? <CheckCircle2 size={13} className="text-[#22C55E]" /> : <XCircle size={13} className="text-[#EF4444]" />}
                <span className={c.ok ? "text-[#A3A3A3]" : "text-[#EF4444]"}>{c.k}</span>
              </div>
            ))}
          </div>
        </div>

        <button data-testid="mission-submit" onClick={submit} disabled={!allOk || sending}
          className="w-full flex items-center justify-center gap-2 bg-[#3B82F6] hover:bg-[#2563EB] disabled:opacity-40 disabled:cursor-not-allowed text-white font-mono uppercase tracking-widest text-xs py-3.5 rounded-sm transition-colors">
          {sending ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />} Lancer la mission
        </button>
        </>)}

        {mode === "itinerary" && (<>
          <div className="grid grid-cols-2 gap-3">
            <Sel label="Type de mission" value={missionType} onChange={setMissionType} testid="itin-type"
              options={MISSION_TYPES.map((m) => ({ v: m.v, l: m.l }))} />
            <div>
              <Lbl>Départ planifié (optionnel)</Lbl>
              <input data-testid="itin-scheduled-start" type="datetime-local" value={scheduledStart}
                onChange={(e) => setScheduledStart(e.target.value)}
                className="w-full bg-[#141414] border border-white/15 focus:border-[#3B82F6] outline-none rounded-sm px-3 py-2.5 font-mono text-sm" />
            </div>
          </div>
          {scheduledStart && (
            <div className="flex items-center gap-1.5 font-mono text-[11px] text-[#F59E0B]">
              <CalendarClock size={13} /> Déclenchement automatique à l'heure prévue (validation live au lancement).
            </div>
          )}

          <div className="space-y-3">
            {waypoints.map((wp, i) => (
              <div key={i} data-testid={`waypoint-${i}`}
                draggable
                onDragStart={() => setDragIdx(i)}
                onDragOver={(e) => e.preventDefault()}
                onDrop={() => { reorderWp(dragIdx, i); setDragIdx(null); }}
                onDragEnd={() => setDragIdx(null)}
                className={`bg-[#0A0A0A] border rounded-sm p-3 space-y-2 transition-colors ${dragIdx === i ? "border-[#3B82F6] opacity-60" : "border-white/10"}`}>
                <div className="flex items-center justify-between">
                  <span className="flex items-center gap-1.5 font-mono text-[11px] uppercase tracking-wider text-[#3B82F6]">
                    <GripVertical size={13} className="text-[#525252] cursor-grab" data-testid={`waypoint-drag-${i}`} /> Waypoint {i + 1}
                  </span>
                  {waypoints.length > 1 && (
                    <button data-testid={`waypoint-remove-${i}`} onClick={() => removeWp(i)} className="text-[#EF4444] hover:text-[#f87171]"><Trash2 size={14} /></button>
                  )}
                </div>
                <div className="flex gap-2 items-end">
                  <div className="flex-1">
                    <input data-testid={`waypoint-address-${i}`} value={wp.address}
                      onChange={(e) => updateWp(i, { address: e.target.value })}
                      onKeyDown={(e) => e.key === "Enter" && geocodeWp(i)}
                      placeholder="Adresse du point"
                      className="w-full bg-[#141414] border border-white/15 focus:border-[#3B82F6] outline-none rounded-sm px-3 py-2 font-mono text-sm" />
                  </div>
                  <button data-testid={`waypoint-geocode-${i}`} onClick={() => geocodeWp(i)} disabled={wp.geocoding}
                    className="flex items-center gap-1 bg-[#3B82F6] hover:bg-[#2563EB] text-white font-mono text-[10px] uppercase rounded-sm px-3 py-2 disabled:opacity-50">
                    {wp.geocoding ? <Loader2 size={13} className="animate-spin" /> : <Search size={13} />}
                  </button>
                </div>
                {wp.dest && (
                  <div data-testid={`waypoint-geocoded-${i}`} className="font-mono text-[10px] text-[#22C55E] flex items-center gap-1"><CheckCircle2 size={11} /> {wp.dest.display_name}</div>
                )}
                <div className="grid grid-cols-3 gap-2">
                  <div>
                    <Lbl>Alt (m)</Lbl>
                    <input data-testid={`waypoint-alt-${i}`} type="number" value={wp.altitude} onChange={(e) => updateWp(i, { altitude: e.target.value })}
                      className="w-full bg-[#141414] border border-white/15 focus:border-[#3B82F6] outline-none rounded-sm px-2 py-2 font-mono text-sm" />
                  </div>
                  <div>
                    <Lbl>Vit (m/s)</Lbl>
                    <input data-testid={`waypoint-speed-${i}`} type="number" value={wp.speed} onChange={(e) => updateWp(i, { speed: e.target.value })}
                      className="w-full bg-[#141414] border border-white/15 focus:border-[#3B82F6] outline-none rounded-sm px-2 py-2 font-mono text-sm" />
                  </div>
                  <div>
                    <Lbl>Heure cible</Lbl>
                    <input data-testid={`waypoint-time-${i}`} type="datetime-local" value={wp.target_time} onChange={(e) => updateWp(i, { target_time: e.target.value })}
                      className="w-full bg-[#141414] border border-white/15 focus:border-[#3B82F6] outline-none rounded-sm px-2 py-2 font-mono text-[11px]" />
                  </div>
                </div>
              </div>
            ))}
            <button data-testid="waypoint-add" onClick={addWp}
              className="w-full flex items-center justify-center gap-2 border border-dashed border-white/20 hover:border-[#3B82F6] text-[#A3A3A3] hover:text-[#3B82F6] font-mono text-[11px] uppercase tracking-wider rounded-sm py-2.5 transition-colors">
              <Plus size={14} /> Ajouter un waypoint
            </button>
          </div>

          {itinSummary && (
            <div data-testid="itinerary-summary" className="bg-[#0A0A0A] border border-[#3B82F6]/30 rounded-sm p-3 space-y-2">
              <div className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.2em] text-[#A3A3A3]"><Route size={12} className="text-[#3B82F6]" /> Récapitulatif itinéraire</div>
              <div className="grid grid-cols-3 gap-2 font-mono text-[11px]">
                <div><div className="text-[#525252] text-[9px] uppercase">Distance</div><div data-testid="itin-total-distance" className="text-white">{num(itinSummary.totalKm, 2)} km</div></div>
                <div><div className="text-[#525252] text-[9px] uppercase">Durée vol</div><div data-testid="itin-total-duration" className="text-white">{fmtDur(itinSummary.totalSec)}</div></div>
                <div><div className="text-[#525252] text-[9px] uppercase">Arrivée finale</div><div data-testid="itin-final-eta" className="text-white">{itinSummary.finalEta ? fmtClock(itinSummary.finalEta) : "—"}</div></div>
              </div>
              <div className="border-t border-white/5 pt-1.5 space-y-1">
                {itinSummary.legs.map((l) => (
                  <div key={l.i} data-testid={`itin-eta-${l.i}`} className="flex items-center justify-between font-mono text-[10px] text-[#A3A3A3]">
                    <span><span className="text-[#3B82F6]">WP{l.i + 1}</span> · {l.i === 0 && !drone?.lat ? "Origine" : `${num(l.legKm, 2)} km`}</span>
                    <span className={l.forced ? "text-[#F59E0B]" : "text-[#737373]"}>
                      {l.forced && "⏱ "}{fmtClock(l.eta)}
                    </span>
                  </div>
                ))}
              </div>
              {!drone?.lat && <div className="font-mono text-[9px] text-[#737373]">Position du drone inconnue : distances estimées depuis le 1er waypoint.</div>}
            </div>
          )}

          <button data-testid="itinerary-submit" onClick={submitItinerary} disabled={sending || waypoints.filter((w) => w.dest).length === 0}
            className="w-full flex items-center justify-center gap-2 bg-[#3B82F6] hover:bg-[#2563EB] disabled:opacity-40 disabled:cursor-not-allowed text-white font-mono uppercase tracking-widest text-xs py-3.5 rounded-sm transition-colors">
            {sending ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />} {scheduledStart ? "Planifier l'itinéraire" : "Lancer l'itinéraire"}
          </button>
        </>)}
      </div>

      {/* Right: map + missions list */}
      <div className="space-y-4">
        <div className="h-[300px] border border-white/10 rounded-sm overflow-hidden">
          <MapView drones={drone ? [drone] : []}
            destination={mode === "single" ? dest : null}
            waypoints={mode === "itinerary" ? waypoints.filter((w) => w.dest).map((w, i) => ({ seq: i, lat: w.dest.lat, lng: w.dest.lng, alt: Number(w.altitude) })) : null}
            plannedPath={mode === "itinerary" ? (() => { const pts = waypoints.filter((w) => w.dest).map((w) => [w.dest.lat, w.dest.lng]); return drone?.lat != null && pts.length ? [[drone.lat, drone.lng], ...pts] : pts; })() : null}
            recenter={mode === "single" && dest ? [dest.lat, dest.lng] : (drone?.lat ? [drone.lat, drone.lng] : null)} />
        </div>

        <div>
          <h2 className="font-chivo text-lg font-bold uppercase tracking-tight mb-2">Missions récentes</h2>
          <div className="space-y-2 max-h-[320px] overflow-y-auto">
            {missions.length === 0 && <div className="font-mono text-xs text-[#525252]">Aucune mission</div>}
            {missions.map((m) => (
              <div key={m.id} data-testid={`mission-item-${m.id}`} className="bg-[#141414] border border-white/10 rounded-sm p-3 font-mono text-xs">
                <div className="flex justify-between items-center">
                  <span className="text-[#3B82F6] uppercase">{m.mission_type}</span>
                  <span className="text-[10px] uppercase tracking-wider" style={{ color: m.status === "completed" ? "#22C55E" : "#F59E0B" }}>{m.status}</span>
                </div>
                <div className="text-[#A3A3A3] mt-1 truncate">{m.address}</div>
                <div className="text-[#525252] mt-0.5">{m.drone_id} · {num(m.altitude, 0)}m · {num(m.speed, 0)}m/s · {num(m.distance_km, 2)}km</div>
                <button data-testid={`replay-${m.id}`} onClick={() => navigate(`/map?mission=${m.id}`)}
                  className="mt-2 flex items-center gap-1.5 border border-[#2DD4BF]/40 text-[#2DD4BF] hover:bg-[#2DD4BF]/15 rounded-sm font-mono text-[10px] uppercase tracking-wider px-2.5 py-1.5 transition-colors">
                  <History size={11} /> Relecture carte
                </button>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function haversine(lat1, lon1, lat2, lon2) {
  const r = 6371, t = (x) => (x * Math.PI) / 180;
  const a = Math.sin(t(lat2 - lat1) / 2) ** 2 + Math.cos(t(lat1)) * Math.cos(t(lat2)) * Math.sin(t(lon2 - lon1) / 2) ** 2;
  return r * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

const Lbl = ({ children }) => <label className="font-mono text-[10px] tracking-[0.2em] uppercase text-[#A3A3A3] block mb-1.5">{children}</label>;

function NumIn({ label, value, onChange, testid }) {
  return (
    <div>
      <Lbl>{label}</Lbl>
      <input data-testid={testid} type="number" value={value} onChange={(e) => onChange(e.target.value)}
        className="w-full bg-[#141414] border border-white/15 focus:border-[#3B82F6] outline-none rounded-sm px-3 py-2.5 font-mono text-sm" />
    </div>
  );
}

function Sel({ label, value, onChange, options, testid }) {
  return (
    <div>
      <Lbl>{label}</Lbl>
      <select data-testid={testid} value={value} onChange={(e) => onChange(e.target.value)}
        className="w-full bg-[#141414] border border-white/15 focus:border-[#3B82F6] outline-none rounded-sm px-3 py-2.5 font-mono text-sm">
        {options.map((o) => <option key={o.v} value={o.v} className="bg-[#141414]">{o.l}</option>)}
      </select>
    </div>
  );
}
